import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, ListGroup, ListRow, TextField } from '@uplink/ui';
import { type FormEvent, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic, inTelegram, openPayUrl } from '../telegram.ts';

/**
 * Sign-in methods in the profile: the email (confirmed or not), a password, and Telegram. On the website a
 * person links Telegram (the bot merges the accounts); in Telegram a person adds an email to sign in on the
 * website.
 */
export function AccountSection() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t } = usePrefs();
  const me = useQuery(trpc.me.queryOptions());
  const [form, setForm] = useState<'login' | 'password' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [current, setCurrent] = useState('');
  const [linkWaiting, setLinkWaiting] = useState(false);

  const refresh = () => queryClient.invalidateQueries(trpc.me.pathFilter());
  const setLogin = useMutation(
    trpc.auth.setLogin.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setForm(null);
        setPassword('');
        refresh();
      },
    }),
  );
  const changePassword = useMutation(
    trpc.auth.changePassword.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setForm(null);
        setPassword('');
        setCurrent('');
      },
    }),
  );
  const resend = useMutation(trpc.auth.resendVerification.mutationOptions());
  const link = useMutation(
    trpc.auth.linkTelegram.mutationOptions({
      onSuccess: (r) => {
        setLinkWaiting(true);
        openPayUrl(r.link);
      },
    }),
  );
  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        queryClient.clear();
        window.location.assign('/');
      },
    }),
  );
  // Linking happens in the bot: look for the result while the person is there
  useQuery({
    ...trpc.me.queryOptions(),
    refetchInterval: linkWaiting && !me.data?.telegramId ? 3000 : false,
  });

  const m = me.data;
  if (!m) return null;

  const submitLogin = (e: FormEvent) => {
    e.preventDefault();
    setLogin.mutate({ email, password });
  };
  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    changePassword.mutate({ current, next: password });
  };

  return (
    <section className="ul-stack" aria-labelledby="account-h">
      <h2 className="ul-group__header" id="account-h">
        {t.account.title}
      </h2>
      <ListGroup
        footer={!m.telegramId ? t.account.telegramFooter : m.email ? undefined : t.account.webFooter}
      >
        {m.email ? (
          <ListRow
            icon="user"
            title={m.email}
            subtitle={m.emailVerified ? undefined : t.account.unconfirmed}
            trailing={
              m.emailVerified ? (
                <Badge tone="ok">{t.account.confirmed}</Badge>
              ) : (
                <button
                  type="button"
                  className="ul-link"
                  disabled={resend.isPending || resend.isSuccess}
                  onClick={() => resend.mutate()}
                >
                  {resend.isSuccess ? t.account.sent : t.account.resend}
                </button>
              )
            }
          />
        ) : (
          <ListRow
            icon="globe"
            title={t.account.webSignIn}
            subtitle={t.account.webSignInText}
            chevron
            onClick={() => setForm(form === 'login' ? null : 'login')}
          />
        )}
        {m.hasPassword ? (
          <ListRow
            icon="key"
            title={t.account.password}
            chevron
            onClick={() => setForm(form === 'password' ? null : 'password')}
          />
        ) : null}
        {m.telegramId ? (
          <ListRow
            icon="bolt"
            title={t.account.telegram}
            value={m.username ? `@${m.username}` : t.account.linked}
          />
        ) : (
          <ListRow
            icon="bolt"
            title={linkWaiting ? t.account.linkWaiting : t.account.linkTelegram}
            chevron={!linkWaiting}
            onClick={() => link.mutate()}
          />
        )}
      </ListGroup>

      {form === 'login' ? (
        <form className="ul-form" onSubmit={submitLogin}>
          <TextField
            id="acc-email"
            label={t.account.email}
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <TextField
            id="acc-password"
            label={t.account.newPassword}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            hint={t.account.passwordHint}
          />
          {setLogin.isError ? (
            <div className="ul-error" role="alert">
              {errorText(setLogin.error)}
            </div>
          ) : null}
          <Button type="submit" block loading={setLogin.isPending}>
            {t.account.save}
          </Button>
        </form>
      ) : null}

      {form === 'password' ? (
        <form className="ul-form" onSubmit={submitPassword}>
          <TextField
            id="acc-current"
            label={t.account.currentPassword}
            type="password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
          />
          <TextField
            id="acc-next"
            label={t.account.newPassword}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            hint={t.account.passwordHint}
          />
          {changePassword.isError ? (
            <div className="ul-error" role="alert">
              {errorText(changePassword.error)}
            </div>
          ) : null}
          <Button type="submit" block loading={changePassword.isPending}>
            {t.account.save}
          </Button>
        </form>
      ) : null}

      {link.isError ? (
        <div className="ul-error" role="alert">
          {errorText(link.error)}
        </div>
      ) : null}

      {!inTelegram && m.web ? (
        <Button variant="ghost" block loading={logout.isPending} onClick={() => logout.mutate()}>
          {t.account.signOut}
        </Button>
      ) : null}
    </section>
  );
}
